<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { VButton, VIcon } from '@/modules/shared/components'
import DisbursementPreferencesImprovedForm from '../components/disbursement-preferences-improved-form.vue'
import { scrollToTop } from '../utils/scroll-to-section'
import IndividualAccountLayout from '../components/individuals/individual-account-layout.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useIndividualStore } from '../stores/better-individual-store'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'

const route = useRoute()
const { individual_id, slug } = route.params as { individual_id: string; slug: string }

const individualStore = useIndividualStore()
const currentIndividual = computed(() => individualStore.individual)
const disbursementPreferencesForm = ref(null)
const inFormMode = ref(false)

const disbursementPreferences = computed(() => {
  if (!currentIndividual.value) return null

  return {
    disbursement_method: currentIndividual.value.disbursement_method,
    disbursement_wire_bank_name: currentIndividual.value.disbursement_wire_bank_name,
    disbursement_wire_bank_address: currentIndividual.value.disbursement_wire_bank_address,
    disbursement_wire_bank_routing_number: currentIndividual.value.disbursement_wire_bank_routing_number,
    disbursement_wire_bank_account_number: currentIndividual.value.disbursement_wire_bank_account_number,
    disbursement_wire_bank_swift_code: currentIndividual.value.disbursement_wire_bank_swift_code,
    disbursement_wire_account_name: currentIndividual.value.disbursement_wire_account_name,
    disbursement_wire_for_further_credit_to: currentIndividual.value.disbursement_wire_for_further_credit_to,
    disbursement_wire_bank_notes: currentIndividual.value.disbursement_wire_bank_notes,
    disbursement_check_receiver_name: currentIndividual.value.disbursement_check_receiver_name,
    disbursement_other_details: currentIndividual.value.disbursement_other_details,
  }
})

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetch = async () => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug })
  hideSkeleton()
}

const editForm = () => {
  inFormMode.value = true
}

const cancelEdit = () => {
  disbursementPreferencesForm.value.resetForm()
  inFormMode.value = false
  scrollToTop()
}

const submit = async (payload) => {
  await individualStore.updateDisbursementPreferences(individual_id, payload, { slug })
  await fetch()
  cancelEdit()
}

const confirmChanges = async (payload) => {
  await disbursementPreferencesForm.value.submit()
}

onMounted(async () => {
  await fetch()
})
</script>

<template>
  <IndividualLayout selectedTab="account">
    <IndividualAccountLayout selectedTab="disbursement-preferences">
      <DisbursementPreferencesImprovedForm
        ref="disbursementPreferencesForm"
        :disbursementPreferences="disbursementPreferences"
        :inDisplayMode="!inFormMode"
        :onSubmit="submit"
      >
        <template #actions>
          <VButton variant="v-white" :click="editForm" v-if="!inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Edit
              <VIcon name="Pencil" />
            </div>
          </VButton>
          <VButton variant="v-blue" size="lg" :click="confirmChanges" v-if="inFormMode">Confirm Changes</VButton>
          <VButton variant="v-white" :click="cancelEdit" v-if="inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Cancel
              <VIcon name="X" />
            </div>
          </VButton>
        </template>
      </DisbursementPreferencesImprovedForm>
    </IndividualAccountLayout>
  </IndividualLayout>
</template>
